import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [testRes, clientRes] = await Promise.all([
          supabase.from('testimonials').select('*').order('created_at', { ascending: false }),
          supabase.from('trusted_clients').select('*').order('created_at', { ascending: false })
        ]);
        
        if (testRes.error) throw testRes.error;
        if (clientRes.error) throw clientRes.error;
        
        setTestimonials(testRes.data || []);
        setClients(clientRes.data || []);
      } catch (err) {
        console.error('Error fetching testimonials data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Client Testimonials & Reviews | HBB0011 REALTY"
        description="Read success stories and testimonials from our esteemed clients. Discover why HBB0011 REALTY is the trusted name in luxury real estate."
        keywords="HBB0011 REALTY reviews, client testimonials, luxury real estate success stories"
      />
      
      {/* 1. Hero Section */}
      <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 lg:min-h-[70vh] flex items-center overflow-hidden">
        
        {/* Background Image Blend */}
        <div className="absolute inset-0 z-0 flex justify-end">
          <div className="w-full lg:w-1/2 h-full relative">
            <img 
              src={buildingImg} 
              alt="Luxury real estate" 
              className="w-full h-full object-cover opacity-20 lg:opacity-50 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 lg:via-[#041424]/20 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-transparent to-transparent"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-8 text-center lg:text-left">
              {/* Breadcrumb */}
              <div className="flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6">
                <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
                <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
                <span className="text-[#D4AF37] uppercase">Testimonials</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white mb-4 tracking-tight uppercase">
                Client Trust. <br className="hidden sm:block"/>
                <span className="font-bold text-[#D4AF37]">Our Greatest Asset.</span>
              </h1>
              
              <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                At HBB0011 Realty Realtors, our clients are at the heart of everything we do. Their trust, satisfaction, and success inspire us to build better every day.
              </p>

              {/* Hero Stats */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 sm:gap-10">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">support_agent</span>
                  <div className="text-left">
                    <div className="text-xl sm:text-2xl font-bold text-white">10+</div>
                    <div className="text-gray-400 text-[9px] sm:text-[10px] uppercase tracking-widest">Happy Clients</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">star</span>
                  <div className="text-left">
                    <div className="text-xl sm:text-2xl font-bold text-white">100%</div>
                    <div className="text-gray-400 text-[9px] sm:text-[10px] uppercase tracking-widest">Client Satisfaction</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">handshake</span>
                  <div className="text-left">
                    <div className="text-xl sm:text-2xl font-bold text-white">Long Term</div>
                    <div className="text-gray-400 text-[9px] sm:text-[10px] uppercase tracking-widest">Relationships</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. Testimonials Grid */}
      <section className="py-16 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="flex items-center justify-center gap-4 mb-16">
          <div className="h-[1px] w-12 sm:w-24 bg-[#D4AF37]/50"></div>
          <h2 className="text-[#D4AF37] text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-center flex items-center gap-2">
            What Our Clients Say
            <span className="material-symbols-outlined bg-[#D4AF37] text-[#041424] rounded-full p-1 text-sm">format_quote</span>
          </h2>
          <div className="h-[1px] w-12 sm:w-24 bg-[#D4AF37]/50"></div>
        </div>

        {loading ? (
           <div className="flex justify-center py-20"><span className="material-symbols-outlined animate-spin text-4xl text-[#D4AF37]">sync</span></div>
        ) : testimonials.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            <p>No testimonials available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-[#06121e] border border-gray-800 hover:border-[#D4AF37]/50 rounded-sm p-6 sm:p-8 transition-colors duration-500 relative flex flex-col justify-between shadow-xl group">
                
                <div className="mb-6">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl mb-4 opacity-80" style={{ fontVariationSettings: "'FILL' 1" }}>format_quote</span>
                  <p className="text-gray-300 text-sm leading-relaxed italic relative z-10 font-light">
                    "{testimonial.quote}"
                  </p>
                </div>

                <div className="flex items-center gap-4 relative z-10 mt-auto">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border border-[#D4AF37] shrink-0">
                    <img src={testimonial.image_url} alt={testimonial.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h3 className="text-white text-xs sm:text-sm font-bold uppercase tracking-widest mb-1">{testimonial.name}</h3>
                    <p className="text-gray-500 text-[9px] sm:text-[10px] uppercase tracking-wider mb-2">{testimonial.role} <br/> {testimonial.location}</p>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} className="material-symbols-outlined text-[#D4AF37] text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </section>

      {/* 3. Stats Strip */}
      <section className="border-t border-b border-gray-800 bg-[#06121e] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 divide-x divide-gray-800">
            
            <div className="flex flex-col items-center justify-center text-center px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">groups</span>
              <div className="text-xl sm:text-2xl font-bold text-white mb-1">10+</div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest">Happy Clients</div>
            </div>

            <div className="flex flex-col items-center justify-center text-center px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">verified</span>
              <div className="text-xl sm:text-2xl font-bold text-white mb-1">100%</div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest">Client Satisfaction</div>
            </div>

            <div className="flex flex-col items-center justify-center text-center px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">workspace_premium</span>
              <div className="text-xl sm:text-2xl font-bold text-white mb-1">1+</div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest">Years of Excellence</div>
            </div>

            <div className="flex flex-col items-center justify-center text-center px-2">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">corporate_fare</span>
              <div className="text-xl sm:text-2xl font-bold text-white mb-1">2000+ Cr</div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest">Total Value Delivered</div>
            </div>

            <div className="flex flex-col items-center justify-center text-center px-2 col-span-2 md:col-span-1 lg:col-span-1 border-l-0 md:border-l md:border-gray-800 lg:border-l">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light mb-3">handshake</span>
              <div className="text-xl sm:text-2xl font-bold text-white mb-1">5000+</div>
              <div className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest">Lives Enriched</div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. Trusted Clients Marquee / Grid */}
      <section className="py-16 bg-[#041424]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-white text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] mb-10">Trusted By Our Valued Clients</h3>
          
          {loading ? (
             <div className="flex justify-center py-10"><span className="material-symbols-outlined animate-spin text-4xl text-[#D4AF37]">sync</span></div>
          ) : clients.length === 0 ? (
            <div className="text-center text-gray-500 py-10">
              <p>No clients available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6">
              {clients.map(client => (
                <div key={client.id} className="border border-gray-800 rounded-sm p-4 flex items-center justify-center bg-[#06121e] hover:border-[#D4AF37]/50 transition-colors h-24 sm:h-32 group relative">
                  <img 
                    src={client.logo_url} 
                    alt={client.name} 
                    className="max-h-full max-w-full object-contain transition-all duration-300 group-hover:scale-105"
                    style={{ filter: client.color_grading !== 'none' ? `${client.color_grading}(100%)` : 'none' }}
                  />
                  {/* Tooltip on hover */}
                  <div className="absolute -bottom-8 bg-[#041424] text-[#D4AF37] text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none border border-[#D4AF37]/30">
                    {client.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 5. Bottom CTA Section (No Photo, highly typographic) */}
      <section className="bg-[#06121e] border-t border-gray-800 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left: Commitment Text */}
            <div className="lg:col-span-5 text-center lg:text-left border-b lg:border-b-0 lg:border-r border-gray-800 pb-10 lg:pb-0 lg:pr-10">
              <h2 className="text-3xl sm:text-4xl text-white font-light mb-4">
                Your Trust. <br className="hidden lg:block"/>
                <span className="font-bold">Our Commitment.</span>
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                We don't just build buildings, we build lasting relationships. 
              </p>
              <div className="mt-4">
                <div className="inline-block relative mb-2">
                  <span className="text-xl sm:text-2xl font-sans font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-yellow-400 uppercase tracking-widest relative z-10">HABEEB RAHIMAN M</span>
                  <div className="absolute -bottom-1 left-0 w-1/3 h-[2px] bg-[#D4AF37]"></div>
                </div>
                <div className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Founder & CEO</div>
              </div>
            </div>

            {/* Right: CTA Grid */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              
              <Link to="/book-visit" className="bg-[#041424] border border-gray-800 hover:border-[#D4AF37] group rounded-sm p-6 flex flex-col justify-between transition-all duration-300">
                <div className="flex justify-between items-start w-full mb-6">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">event</span>
                </div>
                <div>
                  <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-2">Book a Site Visit</h4>
                  <p className="text-gray-500 text-[10px] leading-relaxed mb-6">Experience our projects firsthand.</p>
                </div>
                <div className="w-full flex justify-end">
                  <span className="material-symbols-outlined text-gray-600 group-hover:text-[#D4AF37] transition-colors">arrow_forward</span>
                </div>
              </Link>

              <Link to="/brochure" className="bg-[#041424] border border-gray-800 hover:border-[#D4AF37] group rounded-sm p-6 flex flex-col justify-between transition-all duration-300">
                <div className="flex justify-between items-start w-full mb-6">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">article</span>
                </div>
                <div>
                  <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-2">Download Brochure</h4>
                  <p className="text-gray-500 text-[10px] leading-relaxed mb-6">Get detailed information about our projects.</p>
                </div>
                <div className="w-full flex justify-end">
                  <span className="material-symbols-outlined text-gray-600 group-hover:text-[#D4AF37] transition-colors">arrow_forward</span>
                </div>
              </Link>

              <Link to="/contact" className="bg-[#D4AF37] border border-[#D4AF37] group rounded-sm p-6 flex flex-col justify-between transition-all duration-300 hover:bg-white">
                <div className="flex justify-between items-start w-full mb-6">
                  <span className="material-symbols-outlined text-[#041424] text-3xl font-light">support_agent</span>
                </div>
                <div>
                  <h4 className="text-[#041424] text-xs font-bold uppercase tracking-widest mb-2">Talk to an Expert</h4>
                  <p className="text-gray-800 text-[10px] leading-relaxed mb-6">Our experts are here to help you.</p>
                </div>
                <div className="w-full flex justify-end">
                  <span className="material-symbols-outlined text-[#041424] transition-colors">arrow_forward</span>
                </div>
              </Link>

            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Testimonials;